import { type Artifact } from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import {
  isStructuredSession,
  type LubavOfficeHoursSubsessionType,
  type LubavSessionType,
  type ResourcesContent,
  type StructuredSessionId,
} from '@backrs_ui/grow-ui/src/models/models';
import { type CustomTabId } from '@backrs_ui/grow-ui/src/redux/tabs/customTabIds';
import {
  getTabKey,
  type PageTab,
  type TabItem,
  type TabKey,
} from '@backrs_ui/grow-ui/src/redux/tabs/tabKey';

import {
  ALL_CONTENT_RESOURCE_KEYS,
  type BoxType,
  getBoxOrder,
  getDefaultTabs,
  getInfoBoxConfig,
  type InfoBoxConfigMap,
} from './InfoBox.config';

type SessionContext = Readonly<{
  activeSession: LubavSessionType | null;
  previewedSession: StructuredSessionId | null;
  subsession: LubavOfficeHoursSubsessionType | null;
  hasUserMessages: boolean;
  isLubavActive: boolean;
}>;

type ContentContext = Readonly<{
  resources: ResourcesContent | undefined;
  artifact: Artifact | undefined;
}>;

type ShellContext = Readonly<{
  layout: 'page' | 'resourceTabsOnly';
  staticTabs: readonly CustomTabId[];
  openCustomTabs: readonly CustomTabId[];
  selectedTabKey: TabKey | null;
  coachingTabsAllowed: boolean;
  pageTabs: readonly PageTab[];
  pathname: string;
}>;

const COACHING_TABS: readonly BoxType[] = ['COACHES', 'DEEP_DIVES'];

type ComposeTabsInput = Readonly<{
  shell: ShellContext;
  session: SessionContext;
  content: ContentContext;
  previousAvailableBoxTypes: readonly BoxType[];
}>;

type ComposeTabsOutput = Readonly<{
  tabs: TabItem[];
  availableBoxTypes: BoxType[];
  selectedIndex: number;
  infoBoxConfig: InfoBoxConfigMap;
  tabsWithContent: BoxType[];
  staticTabs: readonly CustomTabId[];
}>;

export const EMPTY_BOX_TYPES: readonly BoxType[] = [];

export const composeTabs = ({
  shell,
  session,
  content,
  previousAvailableBoxTypes,
}: ComposeTabsInput): ComposeTabsOutput => {
  const infoBoxConfig = getInfoBoxConfig(session.subsession, session.activeSession);
  // A previewed session owns the FEATURED tab the same way an active one does.
  const ownsFeaturedTab =
    isStructuredSession(session.activeSession) || session.previewedSession !== null;
  const ideal = withFeaturedTab(
    pickAvailableBoxTypes(shell, session, content, infoBoxConfig),
    ownsFeaturedTab,
  );
  const stabilized = stabilizeWhileStreaming(
    ideal,
    previousAvailableBoxTypes,
    session.isLubavActive,
  );
  const availableBoxTypes =
    session.subsession && !ideal.includes('FEATURED')
      ? stabilized.filter((boxType) => boxType !== 'FEATURED')
      : stabilized;

  const tabs = buildTabItems(shell, availableBoxTypes, ownsFeaturedTab);
  const selectedIndex = resolveSelectedIndex(
    tabs,
    shell.selectedTabKey,
    ownsFeaturedTab,
    shell.pathname,
  );
  const tabsWithContent = availableBoxTypes.filter((boxType) =>
    boxHasContent(boxType, infoBoxConfig, content.resources),
  );

  return {
    tabs,
    availableBoxTypes,
    selectedIndex,
    infoBoxConfig,
    tabsWithContent,
    staticTabs: shell.staticTabs,
  };
};

// Keeps the FEATURED tab available before any artifact exists.
const withFeaturedTab = (boxes: BoxType[], ownsFeaturedTab: boolean): BoxType[] =>
  ownsFeaturedTab && !boxes.includes('FEATURED') ? ['FEATURED', ...boxes] : boxes;

// Picks which resource tabs to surface, given route shape + session phase + current content.
const pickAvailableBoxTypes = (
  shell: ShellContext,
  session: SessionContext,
  content: ContentContext,
  config: InfoBoxConfigMap,
): BoxType[] => {
  const contentBoxes = getBoxOrder(session.subsession, session.activeSession).filter((boxType) =>
    boxHasContent(boxType, config, content.resources, content.artifact),
  );

  // Routes with a real page, or hub routes that already provide their own static tabs,
  // don't need a default-tab seed — just show whatever has content.
  const needsDefaults = shell.layout === 'resourceTabsOnly' && shell.staticTabs.length === 0;

  if (!needsDefaults) return contentBoxes;

  const defaults = defaultBoxTypes(session, config).filter(
    (b) => shell.coachingTabsAllowed || !COACHING_TABS.includes(b),
  );
  const extras = contentBoxes.filter((b) => !defaults.includes(b));

  // Artifact present, OR user has chatted outside a subsession: show only tabs with content.
  const inOpenConversation =
    session.subsession === null && session.hasUserMessages && contentBoxes.length > 0;

  if (content.artifact || inOpenConversation) {
    return contentBoxes;
  }

  // Subsession that has produced output: subsession defaults (with content) first, then extras.
  const inSubsessionWithContent = session.subsession !== null && contentBoxes.length > 0;

  if (inSubsessionWithContent) {
    const defaultsWithContent = defaults.filter((b) => contentBoxes.includes(b));

    return suggestedLast([...defaultsWithContent, ...extras]);
  }

  // Initial state — defaults give the user something to explore on landing.
  return suggestedLast([...defaults, ...extras]);
};

// Default tab set for the current session/subsession. An entered structured session shows FEATURED
// alone; a previewed one leaves the subsession's defaults in place.
const defaultBoxTypes = (session: SessionContext, config: InfoBoxConfigMap): BoxType[] => {
  const tabs = isStructuredSession(session.activeSession)
    ? (['FEATURED'] as const)
    : getDefaultTabs(session.subsession);

  return tabs.filter((boxType) => boxType in config);
};

const suggestedLast = (tabs: BoxType[]): BoxType[] =>
  tabs.includes('SUGGESTED') ? [...tabs.filter((t) => t !== 'SUGGESTED'), 'SUGGESTED'] : tabs;

// named boxes hold featured nuggets, SUGGESTED everything else; FEATURED holds an artifact or
// (for nugget-output sessions) the featured nuggets configured onto it
const boxHasContent = (
  boxType: BoxType,
  config: InfoBoxConfigMap,
  resources: ResourcesContent | undefined,
  artifact?: Artifact,
): boolean => {
  if (boxType === 'FEATURED' && artifact) return true;
  if (boxType === 'SUGGESTED') return hasSuggestedContent(resources);

  return (
    config[boxType]?.resources.some((resourceKey) => {
      const items = resources?.[resourceKey]?.content?.content;

      if (!items?.length) return false;

      return items.some((item) => 'isFeatured' in item && item.isFeatured === true);
    }) ?? false
  );
};

// True if any resource key holds a nugget not marked `isFeatured`.
const hasSuggestedContent = (resources: ResourcesContent | null | undefined): boolean => {
  if (!resources) return false;

  return ALL_CONTENT_RESOURCE_KEYS.some((resourceKey) => {
    const items = resources[resourceKey]?.content?.content;

    return items?.some((item) => !('isFeatured' in item && item.isFeatured === true)) ?? false;
  });
};

// While Lubav is streaming, the tab list can grow but never reorder or shrink — keeps the user from losing their place.
const stabilizeWhileStreaming = (
  ideal: BoxType[],
  previous: readonly BoxType[],
  isLubavActive: boolean,
): BoxType[] => {
  if (!isLubavActive) return ideal;

  const hasNewTabs = ideal.some((tab) => !previous.includes(tab));

  return hasNewTabs ? ideal : [...previous];
};

// Assembles the final ordered tab list. In the resourceTabsOnly layout the lead (first) static
// tab stays first, resource tabs slot in directly after it, then the remaining static tabs, then
// ephemeral custom tabs. In the page layout: static tabs, page tabs (or the single page-content
// tab), resource tabs, ephemeral tabs -- except when the session owns the FEATURED tab, which is
// ordered first.
const buildTabItems = (
  shell: ShellContext,
  availableBoxTypes: readonly BoxType[],
  ownsFeaturedTab: boolean,
): TabItem[] => {
  const resourceItems: TabItem[] = availableBoxTypes.map((boxType) => ({
    kind: 'resource',
    boxType,
  }));
  const staticIds = new Set(shell.staticTabs);
  const staticItems: TabItem[] = shell.staticTabs.map((tabId) => ({ kind: 'custom', tabId }));
  const ephemeralItems: TabItem[] = shell.openCustomTabs
    .filter((id) => !staticIds.has(id))
    .map((tabId) => ({ kind: 'custom', tabId }));

  if (shell.layout === 'resourceTabsOnly') {
    return [
      ...staticItems.slice(0, 1),
      ...resourceItems,
      ...staticItems.slice(1),
      ...ephemeralItems,
    ];
  }

  const pageContent: TabItem[] = shell.pageTabs.length
    ? shell.pageTabs.map((pageTab) => ({ kind: 'page-tab', pageTab }))
    : [{ kind: 'page-content' }];

  if (ownsFeaturedTab) {
    const output = resourceItems.filter((t) => t.kind === 'resource' && t.boxType === 'FEATURED');
    const rest = resourceItems.filter((t) => !(t.kind === 'resource' && t.boxType === 'FEATURED'));

    return [...output, ...staticItems, ...pageContent, ...rest, ...ephemeralItems];
  }

  return [...staticItems, ...pageContent, ...resourceItems, ...ephemeralItems];
};

// Resolves the stored tab key to an index. If the key no longer matches a tab (e.g. a closed
// selector tab), a session owning the FEATURED tab returns to it; otherwise the page tab whose
// route is current, else the first page tab or page-content.
const resolveSelectedIndex = (
  tabs: TabItem[],
  selectedTabKey: TabKey | null,
  ownsFeaturedTab: boolean,
  pathname: string,
): number => {
  if (selectedTabKey) {
    const idx = tabs.findIndex((t) => getTabKey(t) === selectedTabKey);

    if (idx !== -1) return idx;
  }

  if (ownsFeaturedTab) {
    const featuredIdx = tabs.findIndex((t) => t.kind === 'resource' && t.boxType === 'FEATURED');

    if (featuredIdx !== -1) return featuredIdx;
  }

  const currentPageTab = tabs.findIndex(
    (t) => t.kind === 'page-tab' && t.pageTab.path === pathname,
  );

  if (currentPageTab !== -1) return currentPageTab;

  return Math.max(
    tabs.findIndex((t) => t.kind === 'page-content' || t.kind === 'page-tab'),
    0,
  );
};
